import React, { useMemo } from 'react';
import {
	activityNeedsCustomText,
	CHALLENGE_ACTIVITIES,
	COMMUNITY_CHALLENGE_MODES,
	COMMUNITY_DURATION_PRESETS_STANDARD,
	defaultCustomSelection,
	featuredCommunityTemplates,
	findCommunityTemplate,
	mechanicsForActivity,
	unitNeedsCustomText,
	unitsForMechanic
} from '../../constants/communityChallengeTemplates';
import {
	fieldLabelClass,
	hintClass,
	inputClass,
	schedulePanelClass,
	schedulePillActiveClass,
	schedulePillInactiveClass,
	sectionLabelClass,
	templatePillInactiveClass
} from './challengeWireFormStyles';

function schedulePillClass(active) {
	return `rounded-full px-3 py-1.5 text-sm border transition ${
		active ? schedulePillActiveClass : schedulePillInactiveClass
	}`;
}

function modePillClass(active) {
	return `rounded-full border px-3 py-1.5 text-sm font-medium transition ${
		active ? 'border-blue-600 bg-blue-600 text-white' : templatePillInactiveClass
	}`;
}

export default function PostCommunityChallengeConfigurator({
	mode,
	onModeChange,
	activityId,
	onActivityIdChange,
	customActivityText,
	onCustomActivityTextChange,
	mechanic,
	onMechanicChange,
	unitId,
	onUnitIdChange,
	customUnitText,
	onCustomUnitTextChange,
	featuredKey,
	onFeaturedKeyChange,
	templateKey,
	durationDays,
	onDurationDaysChange,
	startsOn,
	onStartsOnChange,
	endsOn,
	title,
	onTitleChange,
	templateCatalog = []
}) {
	const template = useMemo(
		() => findCommunityTemplate(templateKey, templateCatalog),
		[templateKey, templateCatalog]
	);
	const winOptions = useMemo(() => mechanicsForActivity(activityId), [activityId]);
	const unitOptions = useMemo(() => unitsForMechanic(mechanic), [mechanic]);
	const featuredOptions = featuredCommunityTemplates(templateCatalog);
	const featuredTemplate = findCommunityTemplate(featuredKey, templateCatalog);
	const durationPresets = template?.duration_presets?.length
		? template.duration_presets
		: COMMUNITY_DURATION_PRESETS_STANDARD;
	const selectedMechanic = winOptions.find((m) => m.id === mechanic);
	const needsCustomActivityText = activityNeedsCustomText(activityId);
	const needsCustomUnitText = unitNeedsCustomText(unitId);
	const hasActivity = Boolean(activityId);
	const hasMechanic = Boolean(mechanic);

	function handleModeChange(nextMode) {
		onModeChange(nextMode);
		if (nextMode === COMMUNITY_CHALLENGE_MODES.CUSTOM) {
			const defaults = defaultCustomSelection();
			onActivityIdChange(defaults.activityId);
			onMechanicChange(defaults.mechanic);
			onUnitIdChange(defaults.unitId);
			onCustomActivityTextChange('');
			onCustomUnitTextChange('');
		} else if (featuredOptions[0]) {
			onFeaturedKeyChange(featuredOptions[0].key);
		}
	}

	function handleActivityChange(nextActivityId) {
		onActivityIdChange(nextActivityId);
		onMechanicChange('');
		onUnitIdChange('');
		onCustomActivityTextChange('');
	}

	function handleMechanicChange(nextMechanic) {
		onMechanicChange(nextMechanic);
		onUnitIdChange('');
		onCustomUnitTextChange('');
	}

	return (
		<div className="space-y-6">
			<div>
				<p className={sectionLabelClass}>Challenge format</p>
				<p className={`mt-1 ${hintClass}`}>
					Build from what you're doing and how you win, or pick a named community event.
				</p>
				<div className="mt-3 flex flex-wrap gap-2">
					<button
						type="button"
						onClick={() => handleModeChange(COMMUNITY_CHALLENGE_MODES.CUSTOM)}
						className={modePillClass(mode === COMMUNITY_CHALLENGE_MODES.CUSTOM)}
					>
						Build your own
					</button>
					<button
						type="button"
						onClick={() => handleModeChange(COMMUNITY_CHALLENGE_MODES.FEATURED)}
						className={modePillClass(mode === COMMUNITY_CHALLENGE_MODES.FEATURED)}
					>
						Featured event
					</button>
				</div>
			</div>

			{mode === COMMUNITY_CHALLENGE_MODES.CUSTOM ? (
				<div className="space-y-4">
					<label className="block">
						<span className={fieldLabelClass}>Challenge title</span>
						<input
							className={`${inputClass} mt-2`}
							value={title}
							onChange={(e) => onTitleChange(e.target.value)}
							placeholder="Name your challenge"
							maxLength={255}
							required
							aria-required="true"
						/>
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							Required — give your challenge a name before posting.
						</p>
					</label>

					<label className="block">
						<span className={fieldLabelClass}>What are you doing?</span>
						<select
							className={`${inputClass} mt-2`}
							value={activityId}
							onChange={(e) => handleActivityChange(e.target.value)}
						>
							<option value="" disabled>
								Select
							</option>
							{CHALLENGE_ACTIVITIES.map((activity) => (
								<option key={activity.id} value={activity.id}>
									{activity.label}
								</option>
							))}
						</select>
					</label>

					{needsCustomActivityText && (
						<label className="block">
							<span className={fieldLabelClass}>Describe the activity</span>
							<input
								type="text"
								className={`${inputClass} mt-2`}
								value={customActivityText}
								onChange={(e) => onCustomActivityTextChange(e.target.value)}
								placeholder="e.g. one-minute planks, made 3-pointers"
								maxLength={80}
								required
							/>
							<p className={`mt-1.5 ${hintClass}`}>
								Be specific so your network knows what to track.
							</p>
						</label>
					)}

					<label className="block">
						<span className={fieldLabelClass}>How do you win?</span>
						<select
							className={`${inputClass} mt-2`}
							value={mechanic}
							onChange={(e) => handleMechanicChange(e.target.value)}
							disabled={!hasActivity}
						>
							<option value="" disabled>
								Select
							</option>
							{winOptions.map((m) => (
								<option key={m.id} value={m.id}>
									{m.label}
								</option>
							))}
						</select>
						{selectedMechanic?.hint && (
							<p className={`mt-1.5 ${hintClass}`}>{selectedMechanic.hint}</p>
						)}
					</label>

					<label className="block">
						<span className={fieldLabelClass}>Unit of measurement</span>
						<select
							className={`${inputClass} mt-2`}
							value={unitId}
							onChange={(e) => onUnitIdChange(e.target.value)}
							disabled={!hasMechanic}
						>
							<option value="" disabled>
								Select
							</option>
							{unitOptions.map((unit) => (
								<option key={unit.id} value={unit.id}>
									{unit.label}
								</option>
							))}
						</select>
					</label>

					{needsCustomUnitText && (
						<label className="block">
							<span className={fieldLabelClass}>Describe the unit</span>
							<input
								type="text"
								className={`${inputClass} mt-2`}
								value={customUnitText}
								onChange={(e) => onCustomUnitTextChange(e.target.value)}
								placeholder="e.g. floors climbed, calories"
								maxLength={40}
								required
							/>
						</label>
					)}
				</div>
			) : (
				<label className="block">
					<span className={fieldLabelClass}>Pick an event</span>
					<select
						className={`${inputClass} mt-2`}
						value={featuredKey}
						onChange={(e) => onFeaturedKeyChange(e.target.value)}
					>
						{featuredOptions.map((option) => (
							<option key={option.key} value={option.key}>
								{option.label}
							</option>
						))}
					</select>
					{featuredTemplate?.featured_description && (
						<p className={`mt-1.5 ${hintClass}`}>{featuredTemplate.featured_description}</p>
					)}
				</label>
			)}

			<div className={schedulePanelClass}>
				<p className={sectionLabelClass}>Duration</p>
				<div className="flex flex-wrap gap-2 mt-2">
					{durationPresets.map((preset) => (
						<button
							key={preset.days}
							type="button"
							onClick={() => onDurationDaysChange(preset.days)}
							className={schedulePillClass(durationDays === preset.days)}
						>
							{preset.label}
						</button>
					))}
				</div>
				<label className="block mt-4">
					<span className={fieldLabelClass}>Starts</span>
					<input
						type="date"
						className={`${inputClass} mt-2`}
						value={startsOn}
						onChange={(e) => onStartsOnChange(e.target.value)}
					/>
				</label>
				<p className={`mt-2 ${hintClass}`}>Ends {endsOn}</p>
			</div>
		</div>
	);
}
